import {useMatch, useRouterState} from '@tanstack/react-router'
import {useMemo} from 'react'
import {toReactRouterLocation} from './use-location'

export const useNavigation = () => {
  const {routerIsLoading, location} = useRouterState({
    select: s => ({
      routerIsLoading: s.isLoading,
      // `location` is the in-flight target location;
      // use `resolvedLocation` to access the last settled location.
      location: toReactRouterLocation(s.location),
    }),
    // `toReactRouterLocation` builds a new object on every call, so without structural sharing every store
    // emission re-renders this hook. Since TanStack/router#7805, router-core re-publishes the match while a
    // navigation is pending without changing `isLoading`, which would otherwise surface as an extra render.
    structuralSharing: true,
  })
  // Select only `isFetching`: subscribing to the whole match re-renders on every match re-publish
  // (TanStack/router#7805), even when the value this hook reads has not changed.
  const routeMatchIsFetching = useMatch({strict: false, select: match => Boolean(match.isFetching)})
  const isLoading = routerIsLoading || routeMatchIsFetching

  // React Router contract reference:
  // - useNavigation: https://reactrouter.com/api/hooks/useNavigation
  // - Location is undefined when state is 'idle':
  // https://api.reactrouter.com/v7/types/react-router.NavigationStates.html
  // Memoize on the location this hook exposes (`undefined` while idle) so a location-only update
  // while idle keeps the same object, as React Router does.
  const exposedLocation = isLoading ? location : undefined
  return useMemo(
    () =>
      ({
        state: isLoading ? 'loading' : 'idle',
        location: exposedLocation,
      }) as const,
    [isLoading, exposedLocation],
  )
}
